import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  StyleSheet,
  TextInput,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from './DirectionalIcon';
import { colors, spacing, typography } from '../theme';
import { apiService } from '../services/api';
import type { DataList } from '../services/api';
import { useThemedStyles } from '../hooks/useThemedStyles';

const MOBILE_HEADER_OFFSET = 72;

interface QuickAddToListModalProps {
  visible: boolean;
  onClose: () => void;
  contactId: string;
  contactName: string;
  currentUserId?: string;
  onAdded?: () => void;
  onNotify: (type: 'success' | 'error', title: string, message: string) => void;
}

/**
 * Post-call "add to list" sheet — only shows contacts-type Data Lists
 * assigned to the current user (narrower than all contacts lists in the
 * business, per product decision), plus an inline "new list" flow that
 * takes just a name and links the contact directly, mirroring how a system
 * Contacts datalist is created/populated manually elsewhere in the app.
 */
export const QuickAddToListModal: React.FC<QuickAddToListModalProps> = ({
  visible,
  onClose,
  contactId,
  contactName,
  currentUserId,
  onAdded,
  onNotify,
}) => {
  const styles = useThemedStyles(getStyles);
  const [lists, setLists] = useState<DataList[]>([]);
  const [loadingLists, setLoadingLists] = useState(false);
  const [addingListId, setAddingListId] = useState<string | null>(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setCreatingNew(false);
    setNewListName('');
    loadLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const loadLists = async () => {
    setLoadingLists(true);
    try {
      const res = await apiService.getDataLists({ systemModule: 'contacts', assignedUserId: currentUserId });
      if (res.statusCode === 1000 && Array.isArray(res.data)) {
        setLists(res.data);
      }
    } catch (err) {
      console.error('[QuickAddToListModal] Failed to load lists:', err);
    } finally {
      setLoadingLists(false);
    }
  };

  const handleAddToList = async (listId: string) => {
    setAddingListId(listId);
    try {
      const res = await apiService.linkDataListRecords(listId, [contactId]);
      if (res.statusCode === 1000) {
        onNotify('success', 'Added to list', `${contactName} was added.`);
        onAdded?.();
        onClose();
      } else {
        onNotify('error', 'Could not add to list', res.errors?.[0]?.message || 'Please try again.');
      }
    } catch (err: any) {
      console.error('[QuickAddToListModal] Failed to link record:', err);
      onNotify('error', 'Could not add to list', err?.message || 'Please try again.');
    } finally {
      setAddingListId(null);
    }
  };

  const handleCreateAndAdd = async () => {
    const name = newListName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const createRes = await apiService.createDataList({
        name,
        columnsSource: 'system',
        dataSource: 'manual',
        systemModule: 'contacts',
        assignedUserIds: currentUserId ? [currentUserId] : [],
      });
      if (createRes.statusCode !== 1000 || !createRes.data) {
        onNotify('error', 'Could not create list', createRes.errors?.[0]?.message || 'Please try again.');
        return;
      }
      const linkRes = await apiService.linkDataListRecords(createRes.data._id, [contactId]);
      if (linkRes.statusCode === 1000) {
        onNotify('success', 'List created', `${contactName} was added to "${name}".`);
        onAdded?.();
        onClose();
      } else {
        onNotify('error', 'List created, but adding contact failed', linkRes.errors?.[0]?.message || 'Please try again.');
      }
    } catch (err: any) {
      console.error('[QuickAddToListModal] Failed to create list:', err);
      onNotify('error', 'Could not create list', err?.message || 'Please try again.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={onClose}>
        <TouchableOpacity style={styles.modalContainer} activeOpacity={1} onPress={(e) => e.stopPropagation()}>
          <View style={styles.header}>
            <View>
              <Text style={styles.title}>Add to List</Text>
              <Text style={styles.subtitle}>{contactName}</Text>
            </View>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Ionicons name="close" size={24} color={colors.text.primary as string} />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.content}>
            {creatingNew ? (
              <View style={styles.newListRow}>
                <TextInput
                  style={styles.input}
                  value={newListName}
                  onChangeText={setNewListName}
                  placeholder="New list name"
                  placeholderTextColor={colors.text.disabled as string}
                  autoFocus
                />
                <View style={styles.newListActions}>
                  <TouchableOpacity
                    style={styles.cancelNewListButton}
                    onPress={() => { setCreatingNew(false); setNewListName(''); }}
                    disabled={creating}
                  >
                    <Text style={styles.cancelNewListButtonText}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.createButton, (!newListName.trim() || creating) && styles.createButtonDisabled]}
                    onPress={handleCreateAndAdd}
                    disabled={!newListName.trim() || creating}
                  >
                    {creating ? (
                      <ActivityIndicator size="small" color="#FFFFFF" />
                    ) : (
                      <Text style={styles.createButtonText}>Create & Add</Text>
                    )}
                  </TouchableOpacity>
                </View>
              </View>
            ) : (
              <TouchableOpacity style={styles.newListButton} onPress={() => setCreatingNew(true)} activeOpacity={0.75}>
                <Ionicons name="add-circle-outline" size={20} color={colors.primary as string} />
                <Text style={styles.newListButtonText}>New List</Text>
              </TouchableOpacity>
            )}

            <View style={styles.sectionDivider} />

            {loadingLists ? (
              <View style={styles.loadingInline}>
                <ActivityIndicator size="small" color={colors.primary as string} />
                <Text style={styles.loadingInlineText}>Loading your lists…</Text>
              </View>
            ) : lists.length === 0 ? (
              <View style={styles.emptyContainer}>
                <Ionicons name="list-outline" size={40} color={colors.text.disabled as string} />
                <Text style={styles.emptyText}>No contact lists assigned to you yet.</Text>
              </View>
            ) : (
              lists.map((list) => (
                <TouchableOpacity
                  key={list._id}
                  style={styles.listItem}
                  onPress={() => handleAddToList(list._id)}
                  disabled={addingListId === list._id}
                  activeOpacity={0.75}
                >
                  <Ionicons name="list" size={18} color={colors.text.secondary as string} />
                  <Text style={styles.listItemText}>{list.name}</Text>
                  {addingListId === list._id ? (
                    <ActivityIndicator size="small" color={colors.primary as string} />
                  ) : (
                    <DirectionalIcon name="chevron-forward" size={18} color={colors.text.disabled as string} />
                  )}
                </TouchableOpacity>
              ))
            )}
          </ScrollView>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: Platform.OS === 'web' ? 'center' : 'flex-start',
    alignItems: Platform.OS === 'web' ? 'center' : 'stretch',
    padding: Platform.OS === 'web' ? spacing.lg : 0,
    paddingTop: Platform.OS === 'web' ? spacing.lg : 0,
    ...(Platform.OS !== 'web' && {
      position: 'absolute',
      top: MOBILE_HEADER_OFFSET,
      left: 0,
      right: 0,
      bottom: 0,
    }),
  },
  modalContainer: {
    backgroundColor: colors.background.surface,
    borderRadius: Platform.OS === 'web' ? 16 : 0,
    width: '100%',
    maxWidth: Platform.OS === 'web' ? 480 : undefined,
    maxHeight: Platform.OS === 'web' ? '85%' : undefined,
    flex: Platform.OS === 'web' ? undefined : 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border as string,
  },
  title: {
    fontSize: typography.fontSize.xl,
    fontWeight: 'bold' as const,
    color: colors.text.primary as string,
    marginBottom: 4,
  },
  subtitle: { fontSize: typography.fontSize.sm, color: colors.text.secondary as string },
  closeButton: { padding: spacing.xs },
  content: { padding: spacing.lg },
  newListButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.primary as string,
    borderStyle: 'dashed',
  },
  newListButtonText: { fontSize: typography.fontSize.base, fontWeight: '600' as const, color: colors.primary as string },
  newListRow: { gap: spacing.sm },
  newListActions: { flexDirection: 'row', gap: spacing.sm },
  cancelNewListButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.sm,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.border as string,
  },
  cancelNewListButtonText: { color: colors.text.secondary as string, fontSize: typography.fontSize.base, fontWeight: '600' as const },
  createButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.sm,
    borderRadius: 10,
    backgroundColor: colors.primary as string,
  },
  createButtonDisabled: { opacity: 0.5 },
  createButtonText: { color: '#FFFFFF', fontSize: typography.fontSize.base, fontWeight: '600' as const },
  input: {
    borderWidth: 1,
    borderColor: colors.border as string,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    fontSize: typography.fontSize.base,
    color: colors.text.primary as string,
    backgroundColor: colors.background.surface as string,
  },
  sectionDivider: { height: 1, backgroundColor: colors.border as string, marginVertical: spacing.lg },
  loadingInline: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  loadingInlineText: { fontSize: typography.fontSize.sm, color: colors.text.secondary as string },
  emptyContainer: { alignItems: 'center', justifyContent: 'center', paddingVertical: spacing.xl },
  emptyText: { marginTop: spacing.sm, fontSize: typography.fontSize.sm, color: colors.text.secondary as string, textAlign: 'center' as const },
  listItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: 10,
    backgroundColor: colors.background.secondary as string,
    marginBottom: spacing.sm,
  },
  listItemText: { flex: 1, fontSize: typography.fontSize.base, color: colors.text.primary as string },
});
